import {Component, OnInit, OnDestroy} from '@angular/core';
import {PolicyService} from '../../../../policy/services/policy.service';
import {ActivatedRoute} from '@angular/router';
import {GlobalQuestionService} from '../../../services/global-question.service';
import {Alert, firstWhere, getIndex, groupBy, whereEquals} from '../../../../shared/utilities';
import { PolicyPDFScheduleDisclosureNotice, RouterLinks } from '../../../constants';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { StorageService } from '../../../services/storage.service';
import { PolicyScheduleQuery } from '../../../../policy/state/policy-schedule.query';
import { PolicyScheduleStore } from '../../../../policy/state/policy-schedule.store';
import { Subscription } from 'rxjs';
declare const $: any;
@Component({
    selector: 'app-doc-policy-quote',
    templateUrl: './doc-policy-quote.component.html',
    styleUrls: ['./doc-policy-quote.component.scss'],
    standalone: false
})
export class DocPolicyQuoteComponent implements OnInit, OnDestroy {

  id: string;
  session_id: string;
  objectKeys = Object.keys;
  firstWhere = firstWhere;
  whereEquals = whereEquals;
  isTaglineLogo(imageUrl?: string): boolean {
    if (!imageUrl) {
      return false;
    }

    const imagePath = imageUrl.split(/[?#]/, 1)[0];
    return imagePath.split('/').pop() === 'tagline.svg';
  }
  getStatusIndicatorClass(status?: string): string {
    switch (status) {
      case 'Active':
        return 'indicator--positive';
      case 'Pending Active':
        return 'indicator--info';
      case 'Paused':
      case 'Suspended':
        return 'indicator--warning';
      case 'Cancelled':
      case 'Revoked':
      case 'Rejected':
        return 'indicator--error';
      default:
        return '';
    }
  }
  header: any;
  details: any;
  transaction: any;
  coverSummary: any;
  policySummaryAlt: any;
  benefitSummary: any;
  benefitSummaryAlt: any;
  detailsGroups: any[] = [];
  UWNotes: any;
  insuredItems: any[] = [];
  note: any;
  disclosureNotice: any;
  footer: any;
  policyWording: any[] = [];
  policyNotesSummary: any[] = [];
  policyDatasetNineClassWording: any[] = [];
  policyBenefitConditions: any[] = [];
  policyPDFDisclosureNotice: any[] = [];
  // stable reference for <app-disclosure-notice [disclosure]>, recomputed only when
  // policyPDFDisclosureNotice actually changes -- see disclosureNoticeSubscription.
  disclosureNoticeList: any[] = [];
  policySignature: any;
  selectedSection = 'docs-details';
  printDoc = false;
  pdfDoc = false;
  product_id: any;
  // opened in its own tab from the quote page: no Quotes list or login-gated page to go back to
  fromQuotePage = false;
  device: any;
  disclosureNoticeHeaderType = PolicyPDFScheduleDisclosureNotice;
  RouterLinks = RouterLinks; // exposed for the template's breadcrumb routerLink
  // relative (no leading slash) so it resolves against <base href> at runtime;
  // bound via [href] since a static href="..." on a stylesheet <link> is
  // treated by Angular's AOT compiler as a build-time resource path.
  printPdfCssHref: SafeResourceUrl;

  wordingSubscription: Subscription;
  policyNotesSubscription: Subscription;
  datasetNineClassWordingSubscription: Subscription;
  benefitConditionsSubscription: Subscription;
  disclosureNoticeSubscription: Subscription;
  signatureSubscription: Subscription;
  distinctDatasetNineHeaders$: any;

  constructor(
    private route: ActivatedRoute,
    private policyService: PolicyService,
    public questionService: GlobalQuestionService,
    private sanitizer: DomSanitizer,
    private storageService: StorageService,
    private policyScheduleStore: PolicyScheduleStore,
    private policyScheduleQuery: PolicyScheduleQuery
  ) {
    this.printPdfCssHref = this.sanitizer.bypassSecurityTrustResourceUrl('assets/css/print_pdf.css');
    try{
      this.device = this.storageService.getJson('device', 'user');
    }
    catch{}
  }

  ngOnInit(): void {
    this.distinctDatasetNineHeaders$ = this.policyScheduleQuery.selectDatasetNineHeaders$;
    this.route.queryParams
      .subscribe({
        next: value => {
          if (value?.print) {
            this.printDoc = (/true/i).test(value?.print);
          }
          if (value?.pdf) {
            this.pdfDoc = (/true/i).test(value?.pdf);
          }

          this.id = value?.quote_id;
          this.session_id = value?.session_id;
          this.product_id = value?.product_id || null;
          this.fromQuotePage = value?.source === 'quote-page';
         if (this.id || this.session_id) this.getQuoteSchedule();
        }
      });
    this.route.params
      .subscribe({
        next: p => {
          this.questionService.spinner(true);
          if (p?.quoteId) {
            this.id = p?.quoteId;
            if (this.id) this.getQuoteSchedule();
          }
        }
      });
  }

  getQuoteSchedule(): void {
    const request$ = this.session_id
      ? this.policyService.getQuoteScheduleBySessionId(this.session_id, this.product_id)
      : this.policyService.getPolicyQuoteSchedule(this.id, this.product_id);
    request$
      .subscribe({
        next: (schedule) => {
          if (!schedule?.length) {
            $('#error-modal').modal('show');
            $('.error-text').html('Unable to load quote details.');
            this.questionService.spinner(false);
            return;
          }
          this.buildSchedule(schedule);
          if (!this.header?.length) {
            $('#error-modal').modal('show');
            $('.error-text').html('Unable to load quote details.');
          }
        },
        error: (error) => {
          this.questionService.spinner(false);
          $('#error-modal').modal('show');
          $('.error-text').html(error?.error?.Message);
        }
      });
  }

  // mirrors PolicyPdfScheduleComponent.buildSchedule() — same getQuotePolicySchedule proc,
  // resolved via @quote_id (or @session_id for the headless PDF route) instead of a bound
  // policy's @session_id, but the same index/row_group shaped result sets (unified 20260825
  // so a quote-only call runs the exact same pipeline as a bound-policy session).
  buildSchedule(schedule: any): void {
    this.header = getIndex(1, schedule);
    this.details = getIndex(2, schedule);
    this.transaction = getIndex(3, schedule);
    this.coverSummary = getIndex(4, schedule);
    this.policySummaryAlt = getIndex(10, schedule);
    this.benefitSummary = getIndex(5, schedule);
    this.benefitSummaryAlt = getIndex(6, schedule);
    this.detailsGroups = groupBy(this.details, 'subheader');
    this.UWNotes = getIndex(8, schedule);
    this.insuredItems = getIndex(7, schedule);
    this.note = getIndex(14, schedule)[0];
    this.disclosureNotice = getIndex(11, schedule).filter(s => s.header !== 'ALWAYS ON TOP' && s.header !== 'ALWAYS ON BOTTOM');
    this.footer = getIndex(13, schedule)[0];

    const scheduleMain: any[] = [];
    schedule.forEach((element: any, ind: number) => {
      scheduleMain.push({ index: ind + 1, schedule: element });
    });
    this.policyScheduleStore.set(scheduleMain);

    // Distinct classes deduped on class_id rather than on the display name: index 7 names a
    // class with product_class.noun_plu and index 8 with product_class.product_class, and those
    // disagree for 23 classes.
    const distinctListDatasetNine: string[] = [];
    const seenClassIds = new Set<number>();
    this.insuredItems
      .filter((row: any) => row.row_group === 'class' && row.entry_type === 'class_name')
      .forEach((row: any) => {
        if (row?.class_id != null && !seenClassIds.has(Number(row.class_id))) {
          seenClassIds.add(Number(row.class_id));
          distinctListDatasetNine.push(row?.detail);
        }
      });

    // A Simple schedule emits no index 7 at all, so a class that only appears in the wording
    // set would otherwise have no entry to render under.
    getIndex(8, schedule)
      .filter((row: any) => row.header === 'Class Wording' || row.header === 'Item Wording')
      .forEach((row: any) => {
        if (row?.class_id != null && !seenClassIds.has(Number(row.class_id))) {
          seenClassIds.add(Number(row.class_id));
          distinctListDatasetNine.push(row?.class_name);
        }
      });
    this.policyScheduleStore.update({ distinctDatasetNineList: distinctListDatasetNine });

    // keeps the navbar's running total badge in sync while viewing the quote schedule --
    // the only consumer of setQuoteScheduleTotal (see navbar.component.ts's subscription).
    const totalPremium = this.coverRow('premium', 'policy_premium_total')?.premium
      ?? this.coverAltRow('premium', 'policy_premium_total')?.premium;
    if (totalPremium) this.policyService.setQuoteScheduleTotal(totalPremium);

    this.wordingSubscription = this.policyScheduleQuery
      .getWording()
      .subscribe((wording: any[]) => {
        this.policyWording = [];
        if (wording?.length && wording[0]?.schedule?.length) {
          this.policyWording = wording[0]?.schedule;
        }
      });

    this.policyNotesSubscription = this.policyScheduleQuery
      .getPolicyNotes()
      .subscribe((policyNotes: any[]) => {
        this.policyNotesSummary = [];
        if (policyNotes?.length && policyNotes[0]?.schedule?.length) {
          this.policyNotesSummary = policyNotes[0]?.schedule;
        }
      });

    this.datasetNineClassWordingSubscription = this.policyScheduleQuery
      .getDatasetNineClassWording()
      .subscribe((classWording: any[]) => {
        this.policyDatasetNineClassWording = [];
        if (classWording?.length && classWording[0]?.schedule?.length) {
          this.policyDatasetNineClassWording = classWording[0]?.schedule;
        }
      });

    this.benefitConditionsSubscription = this.policyScheduleQuery
      .getBenefitConditions()
      .subscribe((benefitConditions: any[]) => {
        this.policyBenefitConditions = [];
        if (benefitConditions?.length && benefitConditions[0]?.schedule?.length) {
          this.policyBenefitConditions = benefitConditions[0]?.schedule;
        }
      });

    this.disclosureNoticeSubscription = this.policyScheduleQuery
      .getDisclosureNotice()
      .subscribe((disclosureNotice: any[]) => {
        this.policyPDFDisclosureNotice = [];
        if (disclosureNotice?.length && disclosureNotice[0]?.schedule?.length) {
          this.policyPDFDisclosureNotice = disclosureNotice[0]?.schedule;
        }
        // computed once here rather than calling getDisclosureNotice() from the template --
        // a template method call returns a new array (and DisclosureNoticeComponent builds
        // new parsed objects from it) on every change-detection cycle, which makes its
        // track-by-identity @for think the whole list changed every tick (NG0956).
        this.disclosureNoticeList = this.getDisclosureNotice();
      });

    this.signatureSubscription = this.policyScheduleQuery
      .getSignature()
      .subscribe((signature: any[]) => {
        this.policySignature = [];
        if (signature?.length && signature[0]?.schedule?.length) {
          this.policySignature = signature[0]?.schedule[0];
        }
      });

    if (!this.pdfDoc && !this.printDoc) {
      this.questionService.spinner(false);
    }

    setTimeout(() => {
      try{
        this.initDocsNav();
        this.initSearch($('#search-input'));
      }
      catch (e){
        console.log(e)
      }
      if (this.printDoc) {
        window.print();
        this.questionService.spinner(false);
      }
      if (this.pdfDoc) {
        this.preparePDF();
      }
      $('.modal-backdrop').remove();
      $('.page').removeClass('modal-open');
    }, 1000);
  }

  initDocsNav(): void {
    var scrollSpy =  (function() {

      var scrollPos;
      var offset;
      var navTargets = [];

      function init(nav) {

         // offset includes height of fixed nav area
         offset = window.matchMedia('(min-width: 992px)').matches ? $('.breadcrumbs').outerHeight() : $('.layout__sidecol').outerHeight();

         // and add 100px as a threshold
         offset += 100;

         // store target positions
         $(nav).first().find('li').each(function () {
            var target = $(this).find('a').attr('href');
            if (target === '#') return;
            if ($(target).length) {

               let targetPos = Math.floor($(target).offset().top - $('.navbar').outerHeight() - offset - 1)
               if (targetPos < 0) targetPos = 0;

               navTargets.push([target, targetPos]);
            }
         });

         spy(nav);
      }

      function spy(nav) {
         scrollPos = $(window).scrollTop();

         // check each target against scroll position
         navTargets.forEach(function (id, idx) {

            // if scroll below target
            if (scrollPos > navTargets[idx][1]) {

               // if there is a next target
               if ((idx + 1) < navTargets.length) {

                  // check that scroll pos less than next target
                  if (scrollPos < navTargets[idx + 1][1]) {
                     activate(nav, id[0]);
                  }

                  // else no further targets, can activate
               } else {
                  activate(nav, id[0]);
               }

               // else if target is first item
            } else if (idx == 0) {
               activate(nav, id[0]);
            }

         })
      }

      function activate(nav, id) {

         let $activeLi = $(nav).find('li a[href="' + id + '"]').closest('li');

         // activate current li and remove siblings
         $activeLi.addClass('is-spied')
            .siblings('li').removeClass('is-spied')
            .find('li').removeClass('is-spied');

         // activate parents if exist and deactivate siblings and children
         let $activeParentLi = $activeLi.closest('ul').closest('li');
         $activeParentLi
            .addClass('is-spied')
            .siblings('li').removeClass('is-spied')
            .find('li').removeClass('is-spied');
      }
      return {
         init: init,
         spy: spy
      };
   })();
    scrollSpy.init('#docs-nav, #docs-nav-mobile');
    $(window).on('scroll.spy', function () { scrollSpy.spy('#docs-nav, #docs-nav-mobile'); });
    $(window).on('resize.spy', function () { scrollSpy.init('#docs-nav, #docs-nav-mobile'); });

    // activate/deactive nav
    $('.central-nav__list li.has-sub > a').on('click', function (e) {
       e.preventDefault();
       $(this).parent('li').toggleClass('is-active')
          .siblings('li').removeClass('is-active');
    });

 }
 initSearch($input): void {
  $.fn.highlight = function (pat, callback) {

    function innerHighlight(node, pat) {
      var skip = 0;
      if (node.nodeType == 3

        // exclusions:
        // text within dropdowns
        && !node.parentNode.closest('.dropdown-menu')
        && !node.parentNode.closest('.dropdown-item')
      ) {
        node.data = node.data.replace(/[\n\r]+/g, ''); // replace new lines
        node.data = node.data.replace(/\s{2,10}/g, ' '); // replace multiple spaces with just 1
        var pos = node.data.toUpperCase().indexOf(pat);
        pos -= (node.data.substr(0, pos).toUpperCase().length - node.data.substr(0, pos).length);
        if (pos >= 0) {
          var spannode = document.createElement('span');
          spannode.className = 'search-hilite';
          var middlebit = node.splitText(pos);
          var endbit = middlebit.splitText(pat.length);
          var middleclone = middlebit.cloneNode(true);
          spannode.appendChild(middleclone);
          middlebit.parentNode.replaceChild(spannode, middlebit);
          skip = 1;
          if (callback) callback();
        }
      }
      else if (node.nodeType == 1 && node.childNodes && !/(script|style)/i.test(node.tagName)) {
        for (var i = 0; i < node.childNodes.length; ++i) {
          i += innerHighlight(node.childNodes[i], pat);
        }

      }

      return skip;
    }


    return this.length && pat && pat.length ? this.each(function () {
      innerHighlight(this, pat.toUpperCase());
    }) : this;

  };

  $.fn.removeHighlight = function () {
    return this.find("span.search-hilite").each(function () {
      const parent = this.parentNode;
      if (parent) {
        parent.replaceChild(this.firstChild as ChildNode, this);
        parent.normalize();
      }
    }).end();
  };

  $.fn.isInViewport = function () {
    var elementTop = $(this).offset().top;
    var elementBottom = elementTop + $(this).outerHeight();

    var viewportTop = $(window).scrollTop() + $('.navbar').outerHeight();
    var viewportBottom = viewportTop + $(window).height();

    return elementBottom > viewportTop && elementTop < viewportBottom;
  };
  var settings = {
    searchThreshold: 1
  }

  var $searchTrigger = $('#search-trigger, #search-trigger-mobile'),
    $searchContainer = $('#search-container'),
    $inputClear = $('#search-clear'),
    $content = $('#search-body');
  const $searchClose = $('#search-close')

  var result_count = 0,
    curr_result;


  $searchTrigger.off('click').on('click', function (e) {
    e.stopPropagation();

    $searchTrigger.removeClass('is-active');
    $searchContainer.addClass('is-active').closest('.navbar-sub').addClass('search-active');
    $input.focus();

    // listen for outside clicks and close
    $(document).on('click.outsidesearch', function (e) {
      var $target = $(e.target);
      if (!$target.closest(".navbar-sub__search").length && $input.val() == '') {
        resetSearch();
      }
    });

  });

  $inputClear.on('click', function (e) {
    e.preventDefault();
    resetResults();
  });


  $input.on('keyup', function () {

    var search_string = $input.val();
    resetResults();

    // if search bar contains more than x letters
    if (search_string.length > settings.searchThreshold) {

      $content.highlight(search_string, function () {
        result_count++;
      });

      console.log('result count: ' + result_count);

      if (result_count) {
        scrollToResult(1);
      }

    }


  });


  $('.navbar-sub__search-down').on('click', function (e) {
    e.preventDefault();
    goToNextResult();
  });

  $('.navbar-sub__search-up').on('click', function (e) {
    e.preventDefault();
    goToPrevResult();
  });


  $searchClose.on('click', function (e) {
    e.preventDefault();
    resetSearch();
  });


  function goToNextResult() {
    if (curr_result < result_count) {
      scrollToResult(curr_result + 1);
    } else {
      curr_result = 0;
      goToNextResult();
    }
  }

  function goToPrevResult() {
    if (curr_result > 1) {
      scrollToResult(curr_result - 1);
    } else {
      curr_result = result_count + 1;
      goToPrevResult();
    }
  }



  function scrollToResult(resultNo) {

    curr_result = resultNo;

    var $targetEl = $content.find('.search-hilite').eq(resultNo - 1);

    $content.find('.search-hilite').removeClass('is-active');
    $targetEl.addClass('is-active');

    if ($targetEl.isInViewport()) {
      // don't scroll page

      // if not in viewport
    } else {

      if ($targetEl.is(":visible")) {

        let offset = window.matchMedia('(min-width: 992px)').matches ? 50 : $('.layout__sidecol').outerHeight();

        // scroll to target
        $('html, body').scrollTop(
          $targetEl.offset().top - $('.navbar').outerHeight() - offset
        );
      }

    }

  }


  function showNoResults() {
    console.log('no results');
    //$noResults.addClass('is-active');
  }


  function resetResults() {
    result_count = 0;
    $content.removeHighlight();
  }

  function resetSearch() {
    $(document).off('click.outsidesearch');
    $input.val('');
    resetResults();
    $searchTrigger.addClass('is-active');
    $searchContainer.removeClass('is-active').closest('.navbar-sub').removeClass('search-active');
  }

}
  constructID(title: string | null | undefined): string {
    if (!title) return '';
    return `anchor-${title.replace(/ /g,'-')
      .replace(/\//g, "-")
      .replace(/&/g, "-")
      .replace(/\+\./g, "-")
      .replace(/'/g, "")
      .toLowerCase()}`;
  }
  getTranscriptFragment(className: string | null | undefined): string {
    // must match the section anchor id built in policy-transcript.component.html
    return 'docs-' + (className || '').replace(' ', '-');
  }
  // pre-bind quotes have no policy_id yet -- 'Session ID' is a rendered row in the Details
  // table itself (index=2, subheader 'Policy'), so it's already parsed onto `details`.
  getTranscriptQueryParams(): { session_id: string } {
    return { session_id: firstWhere(this.details, 'label', 'Session ID')?.value };
  }

  hasConditions(): boolean {
    return (
      this.policyWording?.length > 0 ||
      this.policyDatasetNineClassWording?.some(
        (itemFilter) =>
          itemFilter.header === 'Class Wording' ||
          itemFilter.header === 'Item Wording'
      )
    );
  }

  getDatasetNineClassWording(item: string): any[] {
    const classId = this.getInsuredItemsClassId(item);
    return this.policyDatasetNineClassWording.filter(
      (itemFilter) =>
        Number(itemFilter.class_id) === Number(classId) &&
        itemFilter.header === 'Class Wording'
    );
  }

  getDatasetNineClassNotes(item: string): any[] {
    const classId = this.getInsuredItemsClassId(item);
    return this.policyDatasetNineClassWording.filter(
      (itemFilter) =>
        Number(itemFilter.class_id) === Number(classId) &&
        itemFilter.header === 'Class Notes'
    );
  }
  getDatasetNineClassItemNotes(
    item: string,
    itemDetailDescription1: string
  ): any[] {
    const classId = this.getInsuredItemsClassId(item);
    return this.insuredItems
      ?.filter(
        (row) =>
          row.row_group === 'item' &&
          row.entry_type === 'note' &&
          row.section === 'notes' &&
          row.class_id === classId &&
          String(row.item_id) === String(itemDetailDescription1)
      )
      .map((row) => ({ note_data: row.detail, amount: row.amount, excess: row.excess })) ?? [];
  }

  // The template addresses classes by name, so resolve name -> id before filtering anything
  // else. Index 7's marker row is the primary source; a Simple schedule has no index 7, so fall
  // back to the wording set, which is the only place its classes appear.
  private getInsuredItemsClassId(className: string): number {
    return this.insuredItems?.find(
      (row) => row.row_group === 'class' && row.entry_type === 'class_name' && row.detail === className
    )?.class_id
      ?? this.policyDatasetNineClassWording?.find(
        (row) => row.class_name === className
      )?.class_id;
  }

  getDistinctDatasetNineClasses(): string[] {
    const classes: string[] = [];
    this.insuredItems?.forEach(row => {
      if (row.row_group === 'class' && row.entry_type === 'class_name' && row.detail && !classes.includes(row.detail)) {
        classes.push(row.detail);
      }
    });
    return classes;
  }

  getDatasetNineClassItemDetail(item: string): any[] {
    const classId = this.getInsuredItemsClassId(item);
    const headers = this.insuredItems?.filter(
      (row) => row.row_group === 'item' && row.entry_type === 'header' && row.class_id === classId
    ) ?? [];
    return headers.map((header) => {
      const fields = this.insuredItems.filter(
        (row) =>
          row.row_group === 'item' &&
          row.entry_type === 'field' &&
          row.section === 'summary' &&
          row.class_id === classId &&
          String(row.item_id) === String(header.item_id)
      );
      return {
        class_id: header.class_id,
        item_id: header.item_id,
        description_1: header.detail,
        premium: header.amount,
        frequency_short: header.frequency_short,
        img_path: header.img_path,
        start_date: fields.find((f) => f.field_key === 'start_date')?.detail,
        sum_insured: fields.find((f) => f.field_key === 'limit')?.detail,
        excess: fields.find((f) => f.field_key === 'excess')?.detail,
        basis: fields.find((f) => f.field_key === 'basis')?.detail,
      };
    });
  }

  getDatasetNineClassIcon(item: string): string {
    return this.policyDatasetNineClassWording?.find(
      (itemFilter) =>
        itemFilter.class_name === item && itemFilter.header === 'Class Header'
    )?.img_path;
  }

  getDatasetNineClassItemExtensions(_item: string, _item_id: string): any[] {
    // TODO: Replace with real API data when available
    // Expected: filter policyDatasetNineClassWording where header === 'Item Extension'
    return [];
  }
  getClassInsurerLogo(className: string): string {
    const classId = this.getInsuredItemsClassId(className);
    return this.insuredItems?.find(
      (row) => row.row_group === 'class' && row.entry_type === 'header' && row.class_id === classId
    )?.class_insurer_img_path ?? '';
  }

  getDatasetNineClassItemTranscript(
    item: string,
    itemDetailDescription1: string
  ): any[] {
    const classId = this.getInsuredItemsClassId(item);
    return this.insuredItems
      ?.filter(
        (row) =>
          row.row_group === 'item' &&
          row.entry_type === 'field' &&
          row.section === 'transcript' &&
          row.class_id === classId &&
          String(row.item_id) === String(itemDetailDescription1)
      )
      .map((row) => ({ question_prompt: row.label, response: row.detail })) ?? [];
  }

  // Conditions Highlights are the item's schedule_highlight-flagged wording on schedule index 7 (section 'conditions'),
  // gated per class by schedule_product_class_config.show_conditions_highlights. The full wording stays in the
  // Conditions section from index 8, so this must not read 'Item Wording' from there.
  getDatasetNineClassItemWording(
    item: string,
    itemDetailDescription1: string
  ): any[] {
    const classId = this.getInsuredItemsClassId(item);
    const money = (v: any): string => {
      const s = String(v ?? '').trim();
      return /^-?\d+(\.\d+)?$/.test(s) ? 'R' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : (s || 'N/A');
    };
    return this.insuredItems
      ?.filter(
        (row) =>
          row.row_group === 'item' &&
          row.entry_type === 'highlight' &&
          row.section === 'conditions' &&
          Number(row.class_id) === Number(classId) &&
          String(row.item_id) === String(itemDetailDescription1)
      )
      .map((row) => ({
        wording_header: row.detail,
        wording_body: row.description,
        wording_amount: money(row.amount),
        wording_excess: money(row.excess),
      })) ?? [];
  }

  getPolicyWording(): any[] {
    return this.policyDatasetNineClassWording?.filter(
      (itemFilter) => itemFilter.header === 'Policy Wording'
    ) ?? [];
  }

  // one row per distinct class that has any Vaps Wording, to drive the outer class loop
  getVapsWordingClasses(): any[] {
    const seen = new Set<number>();
    return this.policyBenefitConditions?.filter((row) => {
      if (seen.has(row.class_id)) return false;
      seen.add(row.class_id);
      return true;
    }) ?? [];
  }

  checkIfVapsWordingExists(): any {
    return this.policyBenefitConditions ?? [];
  }

  // Linked Benefits live on schedule index 7 (section 'linked_benefits'), gated per class by
  // schedule_product_class_config.show_linked_benefits and curated per product_vaps_rel.
  // They used to be read off index 10, which carried them until T-084 retired that set;
  // index 10 now means Policy Summary Alternate, so the old lookup silently matched nothing.
  getInsuredItemLinkedBenefits(
    item: string,
    itemDetailDescription1: string
  ): any[] {
    const classId = this.getInsuredItemsClassId(item);
    return this.insuredItems
      ?.filter(
        (row) =>
          row.row_group === 'item' &&
          row.entry_type === 'linked_benefit' &&
          row.section === 'linked_benefits' &&
          row.class_id === classId &&
          String(row.item_id) === String(itemDetailDescription1)
      )
      .map((row) => ({ vaps_name: row.detail, img_path: row.img_path })) ?? [];
  }

  getDatasetTenBenefitsVapsWording(vapsId: number): any[] {
    return this.policyBenefitConditions?.filter(
      (itemFilter) => itemFilter.vaps_id === vapsId
    ) ?? [];
  }

  // one row per distinct vaps within the class, to drive the per-benefit sub-table loop
  getDatasetTenBenefitsVapsWordingByClass(class_id: number): any[] {
    const seen = new Set<number>();
    return this.policyBenefitConditions?.filter((row) => {
      if (row.class_id !== class_id || seen.has(row.vaps_id)) return false;
      seen.add(row.vaps_id);
      return true;
    }) ?? [];
  }

  // one row per distinct item within the class (item_id = '0' sentinel for policy-level VAPs)
  getBenefitConditionsItems(class_id: number): any[] {
    const seen = new Set<string>();
    return this.policyBenefitConditions?.filter((row) => {
      if (row.class_id !== class_id || seen.has(row.item_id)) return false;
      seen.add(row.item_id);
      return true;
    }) ?? [];
  }

  getInsuredItemDescription(class_id: number, item_id: string): string {
    return this.insuredItems?.find((row: any) =>
      row.row_group === 'item' && row.entry_type === 'header' &&
      row.class_id === class_id && String(row.item_id) === String(item_id)
    )?.detail;
  }

  // one row per distinct vaps within the item, to drive the per-benefit sub-table loop
  getDatasetTenBenefitsVapsWordingByItem(class_id: number, item_id: string): any[] {
    const seen = new Set<number>();
    return this.policyBenefitConditions?.filter((row) => {
      if (row.class_id !== class_id || row.item_id !== item_id || seen.has(row.vaps_id)) return false;
      seen.add(row.vaps_id);
      return true;
    }) ?? [];
  }

  getDatasetTenBenefitsVapsWordingForItem(vapsId: number, item_id: string): any[] {
    return this.policyBenefitConditions?.filter(
      (itemFilter) => itemFilter.vaps_id === vapsId && itemFilter.item_id === item_id
    ) ?? [];
  }

  getImageForClass(item: string): string {
    const classId = this.getInsuredItemsClassId(item);
    const img = this.insuredItems?.find(
      (row) => row.row_group === 'class' && row.entry_type === 'header' && row.class_id === classId
    )?.img_path
      ?? this.policyDatasetNineClassWording?.find(
        (row) => Number(row.class_id) === Number(classId)
      )?.img_path;
    return img ?? 'https://assets.everything.insure/class/sm-0.svg';
  }

  // A summary column switched off in schedule_product_group_config comes back NULL on every row.
  summaryHasColumn(rows: any[], key: 'limit' | 'basis'): boolean {
    return (rows ?? []).some((row: any) => row?.[key] !== null && row?.[key] !== undefined && row?.[key] !== '');
  }

  summaryColspan(rows: any[]): number {
    return 4 + (this.summaryHasColumn(rows, 'limit') ? 1 : 0) + (this.summaryHasColumn(rows, 'basis') ? 1 : 0);
  }

  coverRow(rowGroup: string, entryType: string): any {
    return this.coverSummary?.find((r: any) => r?.row_group === rowGroup && r?.entry_type === entryType);
  }
  coverRows(rowGroup: string, entryType: string): any[] {
    return this.coverSummary?.filter((r: any) => r?.row_group === rowGroup && r?.entry_type === entryType) ?? [];
  }
  coverAltRow(rowGroup: string, entryType: string): any {
    return this.policySummaryAlt?.find((r: any) => r?.row_group === rowGroup && r?.entry_type === entryType);
  }
  coverAltRows(rowGroup: string, entryType: string): any[] {
    return this.policySummaryAlt?.filter((r: any) => r?.row_group === rowGroup && r?.entry_type === entryType) ?? [];
  }
  getPolicySummaryAltClasses(): any[] {
    return this.policySummaryAlt?.filter((r: any) => r?.row_group === 'class' && r?.entry_type === 'class_header') ?? [];
  }
  getPolicySummaryAltItems(classRow: any): any[] {
    return this.policySummaryAlt?.filter((r: any) =>
      r?.row_group === 'class' &&
      r?.entry_type === 'item' &&
      r?.class_id === classRow?.class_id) ?? [];
  }
  // A Simple schedule (index 1 schedule_type) renders the summary as one card per item.
  isSimpleSchedule(): boolean {
    return firstWhere(this.header, 'field_key', 'schedule_type')?.value === 'Simple';
  }
  getSummaryCardItems(): any[] {
    return this.policySummaryAlt?.filter((r: any) => r?.row_group === 'class' && r?.entry_type === 'item') ?? [];
  }
  getSummaryCardFields(item: any): any[] {
    return (this.policySummaryAlt ?? [])
      .filter((r: any) => r?.entry_type === 'item_field' && r?.class_id === item?.class_id && r?.item_id === item?.item_id)
      .sort((a: any, b: any) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
  }
  benefitGroups(): any[] {
    return this.benefitSummary?.filter((r: any) => r?.entry_type === 'header') ?? [];
  }
  benefitSubHeaders(group: any): any[] {
    return this.benefitSummary?.filter((r: any) =>
      r?.entry_type === 'sub_header' &&
      r?.row_group === group?.row_group &&
      (group?.class_id == null || r?.class_id === group?.class_id)) ?? [];
  }
  benefitVaps(sub: any): any[] {
    return this.benefitSummary?.filter((r: any) =>
      r?.entry_type === 'vap' &&
      r?.class_id === sub?.class_id &&
      (r?.item_id ?? null) === (sub?.item_id ?? null)) ?? [];
  }

  // index 6 ("Benefits Summary Alternate") mirrors index 5's row_group/entry_type shape but
  // groups class-level rows by benefit name (sub_header keyed by vap_id) instead of by item.
  getBenefitAltClasses(): any[] {
    return this.benefitSummaryAlt?.filter((r: any) => r?.row_group === 'class' && r?.entry_type === 'header') ?? [];
  }
  getBenefitAltNames(classRow: any): any[] {
    return this.benefitSummaryAlt?.filter((r: any) =>
      r?.row_group === 'class' &&
      r?.entry_type === 'sub_header' &&
      r?.class_id === classRow?.class_id) ?? [];
  }
  getBenefitAltItems(nameRow: any): any[] {
    return this.benefitSummaryAlt?.filter((r: any) =>
      r?.row_group === 'class' &&
      r?.entry_type === 'item' &&
      r?.class_id === nameRow?.class_id &&
      r?.vap_id === nameRow?.vap_id) ?? [];
  }

  getDisclosureNotice(header: string = ''): any[] {
    if (header) {
      return this.policyPDFDisclosureNotice.filter(
        (itemFilter) => itemFilter.header === header
      );
    }

    const distinctNoticeList: any[] = [];
    this.policyPDFDisclosureNotice.forEach((item) => {
      if (
        item?.header !== this.disclosureNoticeHeaderType.ALWAYS_ON_TOP &&
        item?.header !== this.disclosureNoticeHeaderType.ALWAYS_ON_BOTTOM
      ) {
        distinctNoticeList.push(item);
      }
    });

    return distinctNoticeList;
  }

  // Only for when THIS page itself was loaded as the pdf-page view (?pdf=true) -- either
  // Go's own headless capture (?session_id=&headless=true, mirrors policy-pdf-schedule),
  // or a direct browser visit meant to view/print the paginated layout in place.
  // generatePDF() -> runPDFFlow() unconditionally runs initPDFStuff(), which physically
  // splits/duplicates DOM nodes for pagination and is never undone short of a full reload,
  // so closePDFNav()'s reload afterwards is load-bearing here, not optional cleanup.
  // Do NOT wire this to the interactive "Download" button -- see download() below for that.
  // pdf-service/internal/pdfgen/generator.go only understands session_id/policy_id/token_id/
  // document_id as page identifiers (no quote_id), so sessionId always resolves to either
  // the one this page was loaded with, or the one already rendered into the Details table
  // (index=2's 'Session ID' row) when we got here via ?quote_id= instead.
  preparePDF(): void {
    this.questionService.spinner(true);
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.pdfDoc = true;
        // this tab is itself the headless instance Go navigated to capture — just render
        // and signal ready (handled inside generatePDF()); closing/reloading here would
        // fight Go's own capture and, since ?pdf=true is baked into this URL, loop forever.
        const isHeadless = this.route.snapshot.queryParamMap.get('headless') === 'true';
        const sessionId = this.session_id || firstWhere(this.details, 'label', 'Session ID')?.value;
        this.questionService.generatePDF({ sessionId, productId: this.product_id || undefined }).subscribe({
          next: () => {
            if (!isHeadless) {
              this.closePDFNav();
            }
          },
          error: () => Alert('PDF generation failed. Please try again.', 5000),
        });
      });
    }, 1000);
  }

  // The interactive "Download" click (share dropdown / mobile tray menu) while browsing
  // normally. Go renders the PDF in its own separate headless instance regardless of what
  // this tab looks like, so this page's own DOM never needs to become a paginated pdf-page
  // document at all — unlike preparePDF() above, this must NOT touch pdfDoc/initPDFStuff().
  // Mirrors PolicyResourcesComponent.downloadDocumentAsPDF()/openPDFBlob().
  download(): void {
    this.questionService.spinner(true);
    const sessionId = this.session_id || firstWhere(this.details, 'label', 'Session ID')?.value;
    this.questionService.downloadPDF(RouterLinks.PolicyQuoteSchedule, { sessionId, extraParams: this.product_id ? { product_id: `${this.product_id}` } : undefined }).subscribe({
      next: (blob) => {
        this.questionService.spinner(false);
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        this.questionService.showPDFPreview(url);
      },
      error: () => {
        this.questionService.spinner(false);
        Alert('PDF generation failed. Please try again.', 5000);
      },
    });
  }
  closeSchedule(): void {
    if (!this.fromQuotePage) {
      this.questionService?.goBack();
      return;
    }
    window.close();
    // window.close() is ignored for a tab the user opened themselves
    setTimeout(() => {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.questionService?.goBack();
      }
    }, 300);
  }

  closePDFNav(): void {
    this.questionService.spinner(true);
    setTimeout(() => {
      requestAnimationFrame(() => {
        if (this.pdfDoc) {
          this.pdfDoc = false;
          // reloading verbatim would re-read ?pdf=true off this same URL and re-trigger
          // preparePDF() on load, looping forever — strip the one-shot trigger params first.
          const url = new URL(window.location.href);
          url.searchParams.delete('pdf');
          url.searchParams.delete('print');
          window.location.href = url.toString();
        } else {
          this.closeSchedule();
        }
      });
    }, 1000);
  }

  scrollToItem(section: string): void {
    this.selectedSection = section;
    const el = document.getElementById(section);

    if (el) {
      el.scrollIntoView({behavior: 'smooth'});
    }
  }

  ngOnDestroy(): void {
    try {
      this.wordingSubscription?.unsubscribe();
      this.policyNotesSubscription?.unsubscribe();
      this.datasetNineClassWordingSubscription?.unsubscribe();
      this.benefitConditionsSubscription?.unsubscribe();
      this.disclosureNoticeSubscription?.unsubscribe();
      this.signatureSubscription?.unsubscribe();
    } catch {}
  }
}
